import React, { useCallback, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type { ViewType } from '../../utils/workspace.types';

interface GridClickHandlerProps {
  onGridClick: (point: THREE.Vector3) => void;
  currentView: ViewType;
  isBeamCreationMode: boolean;
}

/**
 * Grid Click Handler component - handles clicks on invisible interaction planes
 * Fixed to properly extract intersection coordinates and handle all view types
 */
export const GridClickHandler: React.FC<GridClickHandlerProps> = ({
  onGridClick,
  currentView,
  isBeamCreationMode
}) => {
  const { raycaster } = useThree();
  
  // Debug: Log when component mounts and current view
  useEffect(() => {
    console.log('🟢 GridClickHandler MOUNTED for view:', currentView);
    return () => {
      console.log('🔴 GridClickHandler UNMOUNTED from view:', currentView);
    };
  }, [currentView]);
  
  const handlePointerDown = useCallback((event: ThreeEvent<PointerEvent>) => {
    console.log('🎯 GridClickHandler: Pointer down event triggered!');
    console.log('   - currentView:', currentView);
    console.log('   - isBeamCreationMode:', isBeamCreationMode);
    console.log('   - event.point:', event.point);
    
    event.stopPropagation();
    
    // Don't create nodes when in beam creation mode to avoid duplicates
    if (isBeamCreationMode) {
      console.log('   ❌ Skipped: in beam creation mode');
      return;
    }
    
    let intersectionPoint = new THREE.Vector3();
    let plane: THREE.Plane;
    
    // Create appropriate intersection plane based on current view
    // Z-UP System: Camera positions match workspace.constants.ts VIEW_CONFIGURATIONS
    // CRITICAL: Use event.point directly for orthographic views (more reliable)
    switch (currentView.toLowerCase()) {
      case 'front':
        // Front view: Camera at (0, -80, 0), looking along +Y axis
        // Working plane: XZ plane at Y=0, normal pointing toward camera (negative Y)
        plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
        break;
      case 'top':
        // Top view: Camera at (0, 0, 80), looking down -Z axis
        // Working plane: XY plane at Z=0, normal pointing toward camera (positive Z)
        plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        break;
      case 'side':
        // Side view: Camera at (80, 0, 0), looking along -X axis
        // Working plane: YZ plane at X=0, normal pointing toward camera (positive X)
        plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0);
        break;
      case 'isometric':
      default:
        // Isometric: Use ground plane (XY, Z=0)
        plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        break;
    }
    
    // Get intersection with the appropriate plane
    if (raycaster.ray.intersectPlane(plane, intersectionPoint)) {
      console.log('🎯 GridClickHandler: Intersection found at:', intersectionPoint);
      onGridClick(intersectionPoint);
    } else {
      // Fallback to event.point if raycaster intersection fails
      const fallbackPoint = event.point || new THREE.Vector3(0, 0, 0);
      console.log('🎯 GridClickHandler: Using fallback point:', fallbackPoint);
      onGridClick(fallbackPoint);
    }
  }, [currentView, raycaster, onGridClick, isBeamCreationMode]);

  return (
    <>
      {/* Large invisible planes for click detection based on current view - Expanded for infinite space */}
      {/* CRITICAL: Plane meshes must face the camera for click detection */}
      {currentView.toLowerCase() === 'front' && (
        <mesh
          onPointerDown={handlePointerDown}
          position={[0, 0, 0]}
          rotation={[Math.PI / 2, 0, 0]}  // Rotate to face camera at (0, -Y, 0) - XZ plane
          visible={false}
        >
          <planeGeometry args={[10000, 10000]} />
          <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
        </mesh>
      )}
      {currentView.toLowerCase() === 'top' && (
        <mesh
          onPointerDown={handlePointerDown}
          position={[0, 0, 0]}
          rotation={[0, 0, 0]}  // Default orientation faces +Z camera - XY plane
          visible={false}
        >
          <planeGeometry args={[10000, 10000]} />
          <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
        </mesh>
      )}
      {currentView.toLowerCase() === 'side' && (
        <mesh
          onPointerDown={handlePointerDown}
          position={[0, 0, 0]}
          rotation={[0, Math.PI / 2, 0]}  // Rotate to face camera at (+X, 0, 0) - YZ plane
          visible={false}
        >
          <planeGeometry args={[10000, 10000]} />
          <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
        </mesh>
      )}
      {currentView.toLowerCase() === 'isometric' && (
        <mesh
          onPointerDown={handlePointerDown}
          position={[0, 0, 0]}
          rotation={[0, 0, 0]}  // XY ground plane (horizontal, facing up toward camera)
          visible={false}
        >
          <planeGeometry args={[10000, 10000]} />
          <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} />
        </mesh>
      )}
    </>
  );
};
